/**
 * Analytics Configuration
 * Central configuration for analytics system with environment-based settings
 */

import type { AnalyticsConfig } from "@/types/analytics";

/**
 * Load analytics configuration from environment variables
 */
function loadAnalyticsConfig(): AnalyticsConfig {
  // Analytics is enabled by default in production, can be explicitly disabled
  const enabledEnv = import.meta.env.VITE_ANALYTICS_ENABLED;
  const enabled = enabledEnv === undefined 
    ? import.meta.env.PROD // Default: enabled in prod, disabled in dev
    : enabledEnv === "true" || enabledEnv === "1";

  // Provider defaults to firebase if enabled
  const providerEnv = import.meta.env.VITE_ANALYTICS_PROVIDER;
  const provider = enabled && providerEnv === "firebase" 
    ? "firebase" 
    : "none";

  // Debug mode for development
  const debugEnv = import.meta.env.VITE_ANALYTICS_DEBUG;
  const debug = debugEnv === "true" || debugEnv === "1" || !import.meta.env.PROD;

  return {
    enabled,
    provider,
    debug,
  };
}

/**
 * Global analytics configuration
 * Loaded once at module initialization
 */
export const ANALYTICS_CONFIG: AnalyticsConfig = loadAnalyticsConfig();

/**
 * Check if analytics is enabled and ready to use
 */
export function isAnalyticsEnabled(): boolean {
  return ANALYTICS_CONFIG.enabled && ANALYTICS_CONFIG.provider !== "none";
}

/**
 * Check if analytics debug mode is enabled
 */
export function isAnalyticsDebugMode(): boolean {
  return ANALYTICS_CONFIG.debug;
}
